import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import {
  Building2, Users, Send, MessageSquare, BarChart3, Ticket, Image as ImageIcon,
  FileText, Target, Megaphone, CheckCircle2, ArrowRight, ShieldCheck, TrendingUp,
} from "lucide-react";

const MODULES = [
  { icon: BarChart3, title: "Başvuru Analitiği", desc: "Başvuruları il, sınıf, dönüşüm oranı ile canlı ölçün." },
  { icon: Ticket, title: "Kendi Deneme Sınavlarınız", desc: "Okulunuza özel çevrimiçi sınav oluşturun ve öğrencileri değerlendirin." },
  { icon: ImageIcon, title: "Galeri", desc: "Kampüs ve etkinlik görsellerinizi profilinize ekleyin." },
  { icon: FileText, title: "Broşür", desc: "Tanıtım PDF'inizi tek tıkla ziyaretçilere sunun." },
  { icon: MessageSquare, title: "Veli Mesajlaşma", desc: "Aday veliler ile güvenli, tek panelde mesajlaşın." },
  { icon: Target, title: "Öğrenci Hedefleme", desc: "KVKK uyumlu, anonim profillerle hedef öğrencilere ulaşın." },
  { icon: Megaphone, title: "Duyurular", desc: "Bursluluk kontenjanları, tarihler ve sonuçları paylaşın." },
];

const BENEFITS = [
  "Yıllık binlerce aday veliye erişim",
  "Bursluluk, deneme sınavı, tercih danışmanlığı ile organik trafik",
  "SEO uyumlu okul sayfası + AI destekli otomatik makaleler",
  "Doğrulanmış veli yorumları",
  "Aylık ve yıllık modül fiyatlandırma — istediğinizi seçin",
];

export default function OkullarIcin() {
  return (
    <div data-testid="for-schools-page">
      <SEO
        title="Okullar için SanoStudent — Bursluluk & Kayıt Platformu"
        description="Özel okulunuzu SanoStudent'te ücretsiz listeleyin. Bursluluk başvurularınızı yönetin, aday velilere ulaşın, modüler abonelikle işletmenizi büyütün."
        canonical="/okullar-icin"
        keywords="özel okul için, bursluluk sınavı yönetimi, okul kayıt sistemi, aday veli, okul tanıtımı"
      />

      {/* HERO */}
      <section className="relative overflow-hidden py-20 md:py-28 bg-gradient-to-br from-primary/5 via-background to-accent/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline text-accent mb-3">Okullar İçin</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
              Okulunuzu <span className="text-primary">aday velilere</span> tanıtın.
            </h1>
            <p className="mt-5 text-lg text-muted-foreground max-w-xl">
              SanoStudent, özel okullar için bursluluk sınavı, kayıt yönetimi, veli iletişimi ve
              AI destekli tanıtım araçlarını tek panelde sunar. Ücretsiz başlayın, modüller ile büyütün.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" data-testid="cta-add-school">
                <Link to="/okul-kayit">Okulumu Ekle <ArrowRight className="h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="outline" size="lg" data-testid="cta-school-login">
                <Link to="/giris">Okul Girişi</Link>
              </Button>
            </div>
            <div className="mt-6 flex items-center gap-6 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><ShieldCheck className="h-4 w-4 text-success" /> KVKK Uyumlu</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-success" /> Ücretsiz Kayıt</span>
              <span className="flex items-center gap-1"><TrendingUp className="h-4 w-4 text-success" /> Aylık/Yıllık</span>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-xl">
              <div className="overline text-accent mb-2">Panel Önizleme</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  { l: "Bu ay başvuru", v: "247", trend: "+18%" },
                  { l: "Yeni davet", v: "82", trend: "+9%" },
                  { l: "Aktif sınav", v: "4", trend: null },
                  { l: "Dönüşüm oranı", v: "%38", trend: "+3.4pt" },
                ].map((s) => (
                  <div key={s.l} className="p-3 rounded-lg border border-border bg-background">
                    <div className="text-2xl font-display font-bold">{s.v}</div>
                    <div className="text-xs text-muted-foreground">{s.l}</div>
                    {s.trend && <div className="text-[10px] text-success mt-1">{s.trend}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section className="py-16 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <div className="overline text-accent">Modüler Sistem</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-2">İhtiyacınıza göre modül seçin</h2>
            <p className="text-muted-foreground mt-3 max-w-2xl">
              Sadece kullandığınız modüller için ödeyin. Aylık veya yıllık abonelik. İstediğinizde iptal edin.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {MODULES.map((m) => (
              <div key={m.title} className="p-5 rounded-xl border border-border bg-card hover:border-primary/40 transition">
                <m.icon className="h-7 w-7 text-primary mb-3" />
                <div className="font-display font-bold text-lg">{m.title}</div>
                <div className="text-sm text-muted-foreground mt-1">{m.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12">
          <div>
            <div className="overline text-accent">Neden SanoStudent?</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-2">Okullar için tek panel</h2>
            <p className="text-muted-foreground mt-3">
              Bursluluk sınavlarınızı, başvurularınızı ve tanıtımınızı tek yerden yönetin.
            </p>
          </div>
          <ul className="space-y-3">
            {BENEFITS.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-success mt-0.5 flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FLOW */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="overline text-accent">Nasıl Çalışır?</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-2">3 adımda başlayın</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: 1, t: "Okulunuzu ekleyin", d: "Ücretsiz başvuru formunu doldurun. Yönetici onayı ardından okul paneliniz açılır.", icon: Building2 },
              { n: 2, t: "Modüllerinizi seçin", d: "Bursluluk sınavı, mesajlaşma, analitik gibi modüllerden ihtiyacınızı seçin.", icon: Users },
              { n: 3, t: "Aday velilerle buluşun", d: "Başvurular panelinize düşer, doğrudan mesajlaşabilir ve öğrencileri değerlendirebilirsiniz.", icon: Send },
            ].map((s) => (
              <div key={s.n} className="p-6 rounded-xl border border-border bg-card">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground grid place-content-center font-display font-bold mb-4">{s.n}</div>
                <s.icon className="h-6 w-6 text-accent mb-2" />
                <div className="font-display font-bold text-lg">{s.t}</div>
                <div className="text-sm text-muted-foreground mt-1">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold">Ücretsiz başlayın, dilediğinizde büyütün.</h2>
          <p className="mt-3 opacity-90">Kayıt için kredi kartı gerekmez. Yönetici onayının ardından paneliniz aktif.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg" variant="secondary" data-testid="cta-add-school-2">
              <Link to="/okul-kayit">Okulumu Ekle</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground hover:text-primary">
              <Link to="/nasil-calisir">Nasıl Çalışır?</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
